import type { PrivateSearchResponse } from '@pidelo/contracts';
import { useApiClient, useDebounce } from '@pidelo/web-core';
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { SearchInput } from '../SearchInput/SearchInput.tsx';
import styles from './PrivateSearch.module.css';

type Props = { surface: 'admin' | 'business'; identity: string; onNavigate: (path: string) => void };
type Result = { identity: string; query: string; data?: PrivateSearchResponse; error?: string };
const LABEL = { order: 'Pedido', customer: 'Cliente', driver: 'Conductor', supplier: 'Proveedor', product: 'Producto', dish: 'Plato', ingredient: 'Ingrediente' };

/** A single private search field; the server decides which kinds and records may be shown. */
export function PrivateSearch({ surface, identity, onNavigate }: Props) {
  const api = useApiClient();
  const scope = useSyncExternalStore(api.subscribeSession, api.getSessionScope, api.getSessionScope);
  const key = `${identity}:${scope?.generation ?? 0}`;
  const [q, setQ] = useState('');
  const debounced = useDebounce(q.trim(), 250);
  const [result, setResult] = useState<Result | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  useEffect(() => { if (active >= 0) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' }); }, [active, listId]);
  useEffect(() => { setQ(''); setResult(null); setOpen(false); setActive(-1); }, [key]);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await api.get<PrivateSearchResponse>(`/${surface}/search`, { query: { q: debounced }, signal: controller.signal });
        if (current) {
          setResult({ identity: key, query: debounced, data });
          if (!data.enabled) { setQ(''); setOpen(false); setActive(-1); }
        }
      } catch (error) {
        if (current && !controller.signal.aborted) setResult({ identity: key, query: debounced, error: error instanceof Error ? error.message : 'No pudimos buscar. Intenta de nuevo.' });
      }
    };
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => { current = false; controller.abort(); clearInterval(timer); };
  }, [api, surface, key, debounced]);
  const current = result?.identity === key && result.query === q.trim() ? result : null;
  if (result?.identity === key && result.data?.enabled === false) return null;
  const items = current?.data?.items ?? [];
  const expanded = open && q.trim().length > 0;
  const choose = (index: number) => {
    const item = items[index];
    if (!item) return;
    setOpen(false); setQ(''); setActive(-1);
    onNavigate(item.path);
  };
  return (
    <div className={styles.search} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <SearchInput ref={input} value={q} onChange={value => { setQ(value); setOpen(true); setActive(-1); }}
        placeholder={surface === 'admin' ? 'Buscar en Pídelo' : 'Buscar en tu negocio'} aria-label={surface === 'admin' ? 'Buscar en Pídelo' : 'Buscar en tu negocio'}
        role="combobox" aria-autocomplete="list" aria-expanded={expanded} aria-controls={expanded ? listId : undefined}
        aria-activedescendant={expanded && active >= 0 && items[active] ? `${listId}-${active}` : undefined}
        onFocus={() => setOpen(true)} maxLength={160}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); setOpen(false); setQ(''); setActive(-1); input.current?.focus(); }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); setOpen(true);
            setActive(previous => items.length ? previous < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) : (previous + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length : -1);
          }
          if (event.key === 'Enter' && expanded && active >= 0) { event.preventDefault(); choose(active); }
        }} />
      {expanded ? <div className={styles.results}>
        <p className={styles.status} role="status">
          {!current ? 'Buscando…' : current.error ? current.error : items.length ? `${items.length} ${items.length === 1 ? 'resultado' : 'resultados'}${current.data?.nextCursor ? ' · Escribe más para afinar' : ''}` : 'No hay coincidencias con tus permisos actuales.'}
        </p>
        {items.length ? <ul id={listId} role="listbox" aria-label="Resultados de búsqueda">
          {items.map((item, index) => <li key={`${item.kind}:${item.id}`} id={`${listId}-${index}`} role="option" aria-selected={active === index}>
            <button type="button" tabIndex={-1} className={styles.result} data-active={active === index || undefined} onMouseDown={event => event.preventDefault()} onClick={() => choose(index)}>
              <small>{LABEL[item.kind]}</small><b>{item.title}</b><span>{item.subtitle}</span>
            </button>
          </li>)}
        </ul> : null}
      </div> : null}
    </div>
  );
}
